import { PointerEvent as ReactPointerEvent } from 'react';

import { Camera, CanvasElement } from '@/features/canvas/types';
import { boundsOf, Handle, worldToScreen } from './geometry';

const ACCENT = '#2ec5e8';
const HANDLE = 10; // screen px

/** Elements that expose resize/rotate handles (connectors & freehand are derived — move/delete only). */
const RESIZABLE = new Set(['sticky', 'shape', 'text', 'image']);
/** Content-height cards: only the left/right edges resize (width), no rotate — height is auto-fit. */
const WIDTH_ONLY = new Set(['item', 'search']);
/** Kinds with real geometry, used to frame a multi-selection (connectors are derived → excluded so their
 *  0×0-at-origin box doesn't blow up the bounds). Item/search cards carry real x/y/w/h, so they must be
 *  framed too — otherwise the drawn group box (this set) diverges from the resize pivot (index.tsx uses
 *  SNAP_KINDS, which includes them), causing an instant scale jump and no chrome for a pure card multi-select. */
const GEOM = new Set(['sticky', 'shape', 'text', 'image', 'freehand', 'item', 'search']);

const HANDLES: { h: Handle; cx: number; cy: number; cursor: string }[] = [
  { h: 'nw', cx: 0, cy: 0, cursor: 'nwse-resize' },
  { h: 'n', cx: 0.5, cy: 0, cursor: 'ns-resize' },
  { h: 'ne', cx: 1, cy: 0, cursor: 'nesw-resize' },
  { h: 'e', cx: 1, cy: 0.5, cursor: 'ew-resize' },
  { h: 'se', cx: 1, cy: 1, cursor: 'nwse-resize' },
  { h: 's', cx: 0.5, cy: 1, cursor: 'ns-resize' },
  { h: 'sw', cx: 0, cy: 1, cursor: 'nesw-resize' },
  { h: 'w', cx: 0, cy: 0.5, cursor: 'ew-resize' },
];

// Outward angle of each handle (0°=east, clockwise). CSS `cursor` isn't rotated by the box's transform,
// so on a rotated element we compute the cursor from the handle's angle PLUS the rotation and pick the
// nearest of the four bidirectional resize cursors (they're symmetric, so mod 180°).
const HANDLE_ANGLE: Record<string, number> = { e: 0, se: 45, s: 90, sw: 135, w: 180, nw: 225, n: 270, ne: 315 };
const RESIZE_CURSORS = ['ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize'];
function resizeCursor(handle: Handle, rotationDeg: number): string {
  const base = HANDLE_ANGLE[handle];
  if (base == null) return 'ew-resize';
  let a = (base + rotationDeg) % 180;
  if (a < 0) a += 180;
  return RESIZE_CURSORS[Math.round(a / 45) % 4];
}

interface Props {
  cam: Camera;
  selected: CanvasElement[];
  marquee: { x: number; y: number; w: number; h: number } | null; // screen coords
  onHandlePointerDown: (e: ReactPointerEvent, handle: Handle) => void;
}

/**
 * Selection chrome in SCREEN space, so outlines and handles stay a constant size at any zoom. A single
 * resizable element gets a rotation-following box with 8 resize handles + a rotate handle; multi-select
 * or a connector/freehand gets a plain bounding outline (move/delete only).
 */
export default function SelectionLayer({ cam, selected, marquee, onHandlePointerDown }: Props) {
  const single = selected.length === 1 ? selected[0] : null;
  const widthOnly = !!single && WIDTH_ONLY.has(single.kind);
  const showHandles = !!single && (RESIZABLE.has(single.kind) || widthOnly);
  // All kinds (incl. sticky) get 8 handles — corners scale, edges reflow w/h; item/search cards get only
  // the left/right edges (widen for more text) since their height is content-driven.
  const handles = widthOnly ? HANDLES.filter((h) => h.h === 'e' || h.h === 'w') : HANDLES;
  // Multi-select: frame the real-geometry elements and, when at least one is resizable, offer CORNER
  // handles that scale the whole group proportionally.
  const geomSel = selected.filter((el) => GEOM.has(el.kind));
  const group = !single && geomSel.length > 0 && selected.some((el) => RESIZABLE.has(el.kind));
  const cornerHandles = HANDLES.filter((h) => h.h.length === 2);

  return (
    <div className="pointer-events-none absolute inset-0 z-20">
      {showHandles && single ? (
        <div
          style={{
            position: 'absolute',
            left: worldToScreen(cam, single.x, single.y).x,
            top: worldToScreen(cam, single.x, single.y).y,
            width: single.w * cam.zoom,
            height: single.h * cam.zoom,
            transform: single.rotation ? `rotate(${single.rotation}deg)` : undefined,
            transformOrigin: 'center center',
          }}
        >
          <div className="absolute inset-0 border-2" style={{ borderColor: ACCENT }} />
          {/* rotate handle — not for content-height cards (item/search) */}
          {widthOnly ? null : (
            <>
              <div
                className="pointer-events-auto absolute left-1/2 flex items-center justify-center"
                style={{ top: -30, width: HANDLE + 2, height: HANDLE + 2, marginLeft: -(HANDLE + 2) / 2, cursor: 'grab' }}
                onPointerDown={(e) => onHandlePointerDown(e, 'rotate')}
              >
                <div className="rounded-full border-2 bg-white" style={{ width: HANDLE, height: HANDLE, borderColor: ACCENT }} />
              </div>
              <div className="absolute left-1/2 top-0 -translate-x-px" style={{ height: 20, width: 2, background: ACCENT, transform: 'translateY(-20px)' }} />
            </>
          )}
          {handles.map((hd) => (
            <div
              key={hd.h}
              onPointerDown={(e) => onHandlePointerDown(e, hd.h)}
              className="pointer-events-auto absolute border-2 bg-white"
              style={{
                left: `calc(${hd.cx * 100}% - ${HANDLE / 2}px)`,
                top: `calc(${hd.cy * 100}% - ${HANDLE / 2}px)`,
                width: HANDLE,
                height: HANDLE,
                borderColor: ACCENT,
                cursor: resizeCursor(hd.h, single.rotation ?? 0),
              }}
            />
          ))}
        </div>
      ) : group ? (
        (() => {
          const b = boundsOf(geomSel);
          if (!b) return null;
          const tl = worldToScreen(cam, b.x, b.y);
          return (
            <div style={{ position: 'absolute', left: tl.x, top: tl.y, width: b.w * cam.zoom, height: b.h * cam.zoom }}>
              <div className="absolute inset-0 border-2 border-dashed" style={{ borderColor: ACCENT }} />
              {cornerHandles.map((hd) => (
                <div
                  key={hd.h}
                  onPointerDown={(e) => onHandlePointerDown(e, hd.h)}
                  className="pointer-events-auto absolute border-2 bg-white"
                  style={{
                    left: `calc(${hd.cx * 100}% - ${HANDLE / 2}px)`,
                    top: `calc(${hd.cy * 100}% - ${HANDLE / 2}px)`,
                    width: HANDLE,
                    height: HANDLE,
                    borderColor: ACCENT,
                    cursor: hd.cursor,
                  }}
                />
              ))}
            </div>
          );
        })()
      ) : geomSel.length > 0 ? (
        // A single connector/freehand-only selection has its own handles — only frame REAL-geometry members
        // (avoids a stray 0×0 box at the world origin for a lone connector).
        (() => {
          const b = boundsOf(geomSel);
          if (!b) return null;
          const tl = worldToScreen(cam, b.x, b.y);
          return (
            <div
              className="absolute border-2 border-dashed"
              style={{ left: tl.x, top: tl.y, width: b.w * cam.zoom, height: b.h * cam.zoom, borderColor: ACCENT }}
            />
          );
        })()
      ) : null}

      {marquee ? (
        <div
          className="absolute border"
          style={{
            left: marquee.x,
            top: marquee.y,
            width: marquee.w,
            height: marquee.h,
            borderColor: ACCENT,
            background: `${ACCENT}14`,
          }}
        />
      ) : null}
    </div>
  );
}
